// Layer 1 — Command view (C-suite & board).
// Answers "Are we OK?" — readable in under 30 seconds, no physiological
// knowledge required. Privacy: no individual names, no cohort breakdowns.

import React from "react";
import { Box, Grid, Stack, Typography, alpha, useTheme } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { LineChart } from "../../../../../shared/components/charts";
import {
  AnimatedCounter,
  BulletChart,
  GlassCard,
  MetricRing,
  METRIC_ACCENTS,
  ScanlineBanner,
  SectionHeading,
  TierDot,
  TrendChip,
  tierColor,
} from "./primitives";
import {
  headlineMetrics,
  priorityAlert,
  redTierForecast,
  riskTierSplit,
  workforceReadinessIndex,
} from "../data/mockData";

const shows = [
  "Workforce Readiness Index (composite of all 4 metrics)",
  "Headline KPI for each metric with trend direction",
  "Risk tier workforce split (Green / Amber / Red)",
  "Top-priority alert banner for immediate attention",
];

const hides = [
  "Individual worker names or scores (privacy protected)",
  "Cohort breakdowns or team comparisons",
  "Metric calculation detail or raw data",
  "Intervention recommendations (Operations & Detail only)",
];

export const CommandView: React.FC = () => {
  const theme = useTheme();
  const wriColor = tierColor(workforceReadinessIndex.tier, theme);
  const totalWorkforce = riskTierSplit.reduce((sum, t) => sum + t.count, 0);

  return (
    <Stack spacing={3}>
      <SectionHeading
        overline="Command view · C-suite & board"
        title="Are we OK?"
        subtitle="A 30-second read on workforce fatigue risk. No physiological knowledge required."
      />

      <ScanlineBanner
        tier={priorityAlert.severity}
        title={priorityAlert.title}
        body={priorityAlert.body}
      />

      <Grid container spacing={3}>
        {/* Workforce Readiness Index hero */}
        <Grid size={{ xs: 12, md: 4 }}>
          <GlassCard accent={wriColor} glow>
            <Stack alignItems="center" spacing={1.5} sx={{ py: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Workforce Readiness Index
              </Typography>
              <MetricRing
                value={workforceReadinessIndex.value}
                display={`${workforceReadinessIndex.value}`}
                caption={`${workforceReadinessIndex.label}`}
                color={wriColor}
                size={180}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <TrendChip
                  trend="down"
                  label={`${workforceReadinessIndex.delta} pts vs last week`}
                  goodDirection="up"
                />
              </Stack>
              <Box sx={{ width: "100%", px: 1 }}>
                <BulletChart
                  value={workforceReadinessIndex.value}
                  target={workforceReadinessIndex.target}
                  bands={{ red: 50, amber: 75 }}
                  higherIsBetter
                />
              </Box>
            </Stack>
          </GlassCard>
        </Grid>

        {/* Four headline metric rings */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Grid container spacing={2.5} sx={{ height: "100%" }}>
            {headlineMetrics.map((m) => {
              const accent = METRIC_ACCENTS[m.key];
              return (
                <Grid size={{ xs: 12, sm: 6 }} key={m.key}>
                  <GlassCard accent={accent}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 0.5 }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={800}
                            sx={{
                              color: accent,
                              letterSpacing: 1,
                            }}
                          >
                            {m.code}
                          </Typography>
                          <TrendChip
                            trend={m.trend}
                            label={m.trendLabel}
                            goodDirection={m.goodDirection}
                          />
                        </Stack>
                        <Typography variant="h4" fontWeight={800} lineHeight={1}>
                          <AnimatedCounter value={m.value} />
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {m.unit}
                        </Typography>
                      </Box>
                      <MetricRing
                        value={m.fill}
                        display={m.code}
                        color={accent}
                        size={84}
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1.5 }}
                    >
                      {m.question}
                    </Typography>
                  </GlassCard>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>

      {/* Risk tier split */}
      <GlassCard>
        <SectionHeading
          title="Workforce risk tier split"
          subtitle={`${totalWorkforce} workers across all sites`}
        />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box
            sx={{
              display: "flex",
              height: 18,
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            {riskTierSplit.map((t) => (
              <Box
                key={t.tier}
                sx={{
                  width: `${(t.count / totalWorkforce) * 100}%`,
                  background: `linear-gradient(90deg, ${tierColor(
                    t.tier,
                    theme
                  )}, ${alpha(tierColor(t.tier, theme), 0.7)})`,
                }}
              />
            ))}
          </Box>
          <Grid container spacing={2}>
            {riskTierSplit.map((t) => (
              <Grid size={{ xs: 12, sm: 4 }} key={t.tier}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(tierColor(t.tier, theme), 0.08),
                  }}
                >
                  <TierDot tier={t.tier} pulse={t.tier === "red"} size={14} />
                  <Box>
                    <Typography variant="h5" fontWeight={800}>
                      <AnimatedCounter value={t.count} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.label}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </GlassCard>

      {/* 28-day forecast — proactive, not reactive */}
      <GlassCard glow>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <TrendingUpRoundedIcon sx={{ color: "primary.main" }} />
          <SectionHeading
            title="Red-tier headcount — 28-day forecast"
            subtitle="Predicted from rota, circadian load and recovery debt. Shaded band = confidence interval."
          />
        </Stack>
        <LineChart
          data={redTierForecast}
          xAxisKey="day"
          height={280}
          series={[
            { dataKey: "hi", label: "Upper", color: alpha(theme.palette.error.main, 0.25), area: true, stack: "band" },
            { dataKey: "lo", label: "Lower", color: alpha(theme.palette.background.paper, 0.9), area: true, stack: "band" },
            { dataKey: "actual", label: "Actual", color: theme.palette.text.primary },
            { dataKey: "forecast", label: "Forecast", color: theme.palette.error.main, dashed: true },
          ]}
        />
      </GlassCard>

      {/* What this view shows / does not show */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <GlassCard accent={theme.palette.success.main}>
            <Typography variant="subtitle1" fontWeight={800} gutterBottom>
              This view shows
            </Typography>
            <Stack spacing={1}>
              {shows.map((s) => (
                <Stack key={s} direction="row" spacing={1} alignItems="flex-start">
                  <CheckCircleRoundedIcon
                    fontSize="small"
                    sx={{ color: "success.main", mt: 0.2 }}
                  />
                  <Typography variant="body2">{s}</Typography>
                </Stack>
              ))}
            </Stack>
          </GlassCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <GlassCard accent={theme.palette.error.main}>
            <Typography variant="subtitle1" fontWeight={800} gutterBottom>
              This view does NOT show
            </Typography>
            <Stack spacing={1}>
              {hides.map((s) => (
                <Stack key={s} direction="row" spacing={1} alignItems="flex-start">
                  <CancelRoundedIcon
                    fontSize="small"
                    sx={{ color: "error.main", mt: 0.2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {s}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </GlassCard>
        </Grid>
      </Grid>
    </Stack>
  );
};
